/**
 * Turning conversations into the list's rows: the name, the faces and the
 * preview each come from a different place, and none of them is the row the
 * server sent on its own.
 *
 * Pure, and kept out of the connected view so it can be tested without pulling
 * the directory graph in (as `hooks/peerProfiles.ts` is, for the same reason).
 */
import type { Conversation, LastEvent, PeerProfile } from "../../types"
import type { Jid } from "../../xmpp/jid"
import type { ConversationRowModel } from "../ConversationList"
import type { Translate } from "./translate"

type ConversationRowsContext = {
  resolvePeer: (peer: Jid) => PeerProfile
  resolveHandle: (handle: string) => PeerProfile
  ownHandle: string | undefined
  t: Translate
}

export function toConversationRows(
  conversations: Conversation[],
  { resolvePeer, resolveHandle, ownHandle, t }: ConversationRowsContext
): ConversationRowModel[] {
  return conversations.map((conversation) => {
    const members = conversation.members

    return {
      peer: conversation.peer,
      name: displayName(conversation, resolvePeer),
      faces: members
        ? members.map(resolveHandle)
        : [resolvePeer(conversation.peer)],
      preview: preview(conversation, resolveHandle, ownHandle, t),
      timestamp: conversation.lastTimestamp,
      isUnread: conversation.unread,
      isMuted: conversation.muted ?? false,
    }
  })
}

/**
 * The last activity, as the row reports it. A deleted message reads as a stub;
 * a group event (FAME-703) is localized from its structured form; otherwise it
 * is the message text (FAME-657). The three sources never coexist.
 *
 * A plain message is prefixed with its sender (FAME-714): "You: " for your own,
 * the sender's name in a group. A peer's message in a DM gets no prefix — the
 * row is already titled with their name. The prefix waits on `lastAuthor`, which
 * a server reseed does not carry, so it appears once a live message lands.
 */
function preview(
  conversation: Conversation,
  resolveHandle: (handle: string) => PeerProfile,
  ownHandle: string | undefined,
  t: Translate
): string {
  if (conversation.lastRetracted) return String(t("chat.MESSAGE_DELETED"))
  if (conversation.lastEvent)
    return eventPreview(conversation.lastEvent, resolveHandle, t)

  // A caption is the better preview; an image without one is named instead.
  const body =
    conversation.lastBody ||
    (conversation.lastAttachment ? String(t("chat.PREVIEW_IMAGE")) : "")
  const author = conversation.lastAuthor
  if (!author) return body
  if (author === ownHandle) return String(t("chat.PREVIEW_OWN", { body }))
  if (conversation.type === "group")
    return String(
      t("chat.PREVIEW_SENDER", {
        name: resolveHandle(author).displayName,
        body,
      })
    )
  return body
}

/**
 * A group event as the row's preview — the same phrasing as the in-thread
 * notice, but from the row's structured `last_event` rather than a message.
 */
function eventPreview(
  event: LastEvent,
  resolveHandle: (handle: string) => PeerProfile,
  t: Translate
): string {
  const by = resolveHandle(event.by).displayName

  switch (event.type) {
    case "archived":
      return String(t("chat.NOTICE_ARCHIVED", { by }))
    case "unarchived":
      return String(t("chat.NOTICE_UNARCHIVED", { by }))
    case "renamed":
      return String(t("chat.NOTICE_RENAMED", { by, name: event.name }))
    case "member_added":
      return String(
        t("chat.NOTICE_MEMBER_ADDED", {
          by,
          member: resolveHandle(event.member).displayName,
        })
      )
    case "member_removed":
      return String(
        t("chat.NOTICE_MEMBER_REMOVED", {
          by,
          member: resolveHandle(event.member).displayName,
        })
      )
    case "member_left":
      // Leaving names only the person who left, not an actor.
      return String(
        t("chat.NOTICE_MEMBER_LEFT", {
          member: resolveHandle(event.member).displayName,
        })
      )
    case "promoted":
      return String(
        t("chat.NOTICE_PROMOTED", {
          by,
          member: resolveHandle(event.member).displayName,
        })
      )
    case "demoted":
      return String(
        t("chat.NOTICE_DEMOTED", {
          by,
          member: resolveHandle(event.member).displayName,
        })
      )
  }
}

/** The handles a group event's preview names, for the resolution batch. */
export function eventHandles(event: LastEvent | undefined): string[] {
  if (!event) return []
  if (
    event.type === "member_added" ||
    event.type === "member_removed" ||
    event.type === "member_left" ||
    event.type === "promoted" ||
    event.type === "demoted"
  )
    return [event.by, event.member]
  return [event.by]
}

/** A group is named by the server; a person by the directory. */
function displayName(
  conversation: Conversation,
  resolvePeer: (peer: Jid) => PeerProfile
): string {
  return conversation.name ?? resolvePeer(conversation.peer).displayName
}
